import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import {
  Employee,
  TechnicianGroup,
  AppointmentCase,
  CampaignHeader,
  ServiceGroup,
  Vehicle,
  WarrantySubmissionType,
} from '@quorum/models/xs-resource';
import { createFormGroupFromObject } from 'libs/sha-services/src/lib/createFormGroupFromObject';
import * as moment from 'moment';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { debounceTime, filter, map, switchMap, take, tap } from 'rxjs/operators';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { ServiceGroupSelectionComponent } from '../service-group-selection/service-group-selection.component';

@Component({
  selector: 'sha-appointment-case-edit-form',
  templateUrl: './appointment-case-edit-form.component.html',
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('500ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('500ms', style({ opacity: 0 }))]),
    ]),
  ],
  styleUrls: ['./appointment-case-edit-form.component.scss'],
})
export class AppointmentCaseEditFormComponent implements OnInit, OnDestroy {
  @Input() case: AppointmentCase;
  @Input() dropdowns$: Observable<any>;
  @Input() serviceGroups$: Observable<ServiceGroup[]>;
  @Input() vehicle$: Observable<Vehicle>;
  @Output() saveCase: EventEmitter<any> = new EventEmitter();
  caseForm: FormGroup;
  authenticatedEmployee$: Observable<Employee>;
  filteredServiceGroups$: Observable<ServiceGroup[]>;
  tenthsMask = createNumberMask({
    prefix: '',
    allowDecimal: true,
    requireDecimal: true,
    decimalLimit: 2,
    allowNegative: false,
  });
  warrantySubmissionTypeSub: Subscription;
  basicWarrantyExpired: boolean;
  caseFormOpCodeSub: Subscription;
  caseFormCampaignNumberSub: Subscription;

  constructor(
    public dialog: MatDialog,
    private authenticationStateService: AuthenticationStateService,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.caseForm = createFormGroupFromObject(this.case);
    this.checkTenths(['estimatedTime', 'recommendedHours']);
    this.caseForm
      .get('startTime')
      .setValue(
        this.case.startTime
          ? (new Date(this.case.startTime).getHours() < 10 ? '0' : '') +
              new Date(this.case.startTime).getHours() +
              ':' +
              (new Date(this.case.startTime).getMinutes() < 10 ? '0' : '') +
              new Date(this.case.startTime).getMinutes()
          : null
      );
    this.caseForm.addControl(
      'techOrTechGroup',
      new FormControl(this.case.technicianId ? this.case.technicianId : this.case.technicianGroupId)
    );

    if (this.caseForm.get('warrantySubmissionTypeId').value === 'CC') {
      this.caseForm.get('warrantySubmissionTypeId').disable({ emitEvent: false });
    } else {
      this.caseForm.get('campaignNumber').disable({ emitEvent: false });
    }
    this.setFormValidation();

    this.authenticatedEmployee$ = this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(filter((authEmployee) => authEmployee != null));
    this.filteredServiceGroups$ = combineLatest(this.serviceGroups$, this.authenticatedEmployee$).pipe(
      map(([serviceGroups, employee]) => {
        return serviceGroups.filter((sg) => sg.rooftopId == employee.rooftopId || sg.rooftopId == -1);
      })
    );
    // combineLatest(
    //   this.caseForm.get('serviceGroupId').valueChanges.pipe(
    //     filter(value => {
    //       return value != 'lookup' && value != null;
    //     })
    //   ),
    //   this.serviceGroups$
    // )
    //   .pipe(
    //     tap(([value, serviceGroups]) => {
    //       let serviceGroup: any = serviceGroups.find(sg => sg.id == value);
    //       let serviceParams: ServiceQueryParameters = new ServiceQueryParameters();
    //       serviceParams.id = serviceGroup.services.map((s: any) => s.serviceId).join(',');

    //       this.apiService.get(`v/1/fixed-operations/services`, { params: serviceParams }).subscribe(services => {
    //         this.caseForm.get('requestDescription').setValue('');
    //         let newDescription: string = `${
    //           this.caseForm.get('requestDescription').value ? this.caseForm.get('requestDescription').value + ' - ' : ''
    //         } ${serviceGroup.name} (${services.map((s: any) => s.name.trim()).join(', ')})`;
    //         this.caseForm.get('requestDescription').setValue(newDescription);
    //       });
    //     })
    //   )
    //   .subscribe();

    this.caseFormOpCodeSub = combineLatest(
      this.caseForm.get('opCode').valueChanges.pipe(filter((opCode) => opCode && opCode.length > 0)),
      this.authenticatedEmployee$.pipe(take(1)),
      this.filteredServiceGroups$.pipe(take(1))
    )
      .pipe(
        map(([opCode, employee, filteredServiceGroups]) => {
          return filteredServiceGroups.find(
            (serviceGroup) =>
              serviceGroup.workOrderLocationOpCode === opCode &&
              (serviceGroup.rooftopId === employee.rooftopId || serviceGroup.rooftopId == -1)
          );
        })
      )
      .subscribe((serviceGroup: ServiceGroup) => {
        if (serviceGroup) {
          if (serviceGroup.technicianId) {
            this.caseForm.get('techOrTechGroup').setValue(serviceGroup.technicianId);
            this.caseForm.get('technicianId').setValue(serviceGroup.technicianId);
          } else if (serviceGroup.technicianGroupId) {
            this.caseForm.get('technicianGroupId').setValue(serviceGroup.technicianGroupId);
            this.caseForm.get('techOrTechGroup').setValue(serviceGroup.technicianGroupId);
          } else {
            this.caseForm.get('technicianGroupId').setValue(null);
            this.caseForm.get('techOrTechGroup').setValue(null);
          }
          this.caseForm.get('serviceGroupId').setValue(serviceGroup.id);
          this.caseForm.get('warrantySubmissionTypeId').setValue(serviceGroup.warrantySubmissionTypeId);
          this.caseForm.get('estimatedTime').setValue(serviceGroup.estimatedTime);
          this.caseForm.get('recommendedHours').setValue(serviceGroup.recommendedHours);
          this.caseForm.get('requestDescription').setValue(serviceGroup.name);
          this.checkTenths(['estimatedTime', 'recommendedHours']);
        }
      });

    this.caseFormCampaignNumberSub = this.caseForm
      .get('campaignNumber')
      .valueChanges.pipe(
        tap((value) => {
          if (value && value.length > 0) {
            this.caseForm.get('warrantySubmissionTypeId').setValue('CC', { emitEvent: false });
            this.caseForm.get('warrantySubmissionTypeId').disable({ emitEvent: false });
          } else {
            this.caseForm.get('warrantySubmissionTypeId').setValue(null, { emitEvent: false });
            this.caseForm.get('warrantySubmissionTypeId').enable({ emitEvent: false });
          }
        }),
        filter((campaignNumber) => campaignNumber && campaignNumber.length > 0),
        debounceTime(1000)
      )
      .pipe(
        switchMap((campaignNumber) => {
          return this.apiService.get<any>(`v/2/fixed-operations/campaign-headers/${campaignNumber}`);
        })
      )
      .subscribe((campaign: CampaignHeader) => {
        if (campaign) {
          if (campaign.technicianId) {
            this.caseForm.get('techOrTechGroup').setValue(campaign.technicianId);
            this.caseForm.get('technicianId').setValue(campaign.technicianId);
          } else if (campaign.technicianGroupId) {
            this.caseForm.get('technicianGroupId').setValue(campaign.technicianGroupId);
            this.caseForm.get('techOrTechGroup').setValue(campaign.technicianGroupId);
          } else {
            this.caseForm.get('technicianGroupId').setValue(null);
            this.caseForm.get('techOrTechGroup').setValue(null);
          }
          this.caseForm.get('estimatedTime').setValue(campaign.estimatedTime);
          this.caseForm.get('recommendedHours').setValue(campaign.recommendedHours);
          this.caseForm.get('opCode').setValue(campaign.operationCode);
          this.caseForm.get('requestDescription').setValue(campaign.description);
          this.checkTenths(['estimatedTime', 'recommendedHours']);
        }
      });

    this.warrantySubmissionTypeSub = combineLatest(
      this.caseForm.get('warrantySubmissionTypeId').valueChanges.pipe(filter((value) => value != null)),
      this.vehicle$,
      this.dropdowns$
    )
      .pipe(
        map(([warrantySubmissionTypeId, vehicle, dropdowns]) => {
          return { warrantySubmissionTypeId, vehicle, dropdowns };
        }),
        map((data: any) => {
          let selectedWarrantyType = data.dropdowns.submissionTypes.find(
            (subType: WarrantySubmissionType) => subType.id === data.warrantySubmissionTypeId
          );
          if (!selectedWarrantyType.description.toLowerCase().includes('campaign')) {
            this.caseForm.get('campaignNumber').setValue(null, { emitEvent: false });
            this.caseForm.get('campaignNumber').disable({ emitEvent: false });
          } else {
            this.caseForm.get('campaignNumber').setValidators(Validators.required);
            this.caseForm.get('campaignNumber').enable({ emitEvent: false });
          }
          if (selectedWarrantyType.description.toLowerCase().includes('warranty')) {
            combineLatest(
              this.apiService.get(`v/1/reporting/vehicles/workflow/warranties/${data.vehicle.id}`),
              this.apiService.get(`v/1/vehicles/vehicles/${data.vehicle.id}`, { params: { embed: 'associate' } })
            )
              .pipe(
                map(([warrantyInfo, vehicle]) => {
                  return {
                    warrantyInfo: warrantyInfo,
                    vehicle: vehicle,
                  };
                }),
                take(1),
                tap((data) => {
                  if (data.warrantyInfo.regularWarranty) {
                    let date = moment(data.vehicle.warrantyEffectiveDate).add(
                      data.warrantyInfo.regularWarranty.warrantyTerm,
                      'months'
                    );

                    if (
                      date.isAfter(new Date()) &&
                      data.warrantyInfo.regularWarranty.warrantyMileage > data.vehicle.currentMileage
                    ) {
                      this.basicWarrantyExpired = false;
                    } else {
                      this.basicWarrantyExpired = true;
                      this.cdr.detectChanges();
                    }
                  }
                })
              )
              .subscribe();
          } else {
            this.basicWarrantyExpired = false;
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.warrantySubmissionTypeSub.unsubscribe();
    this.caseFormOpCodeSub.unsubscribe();
    this.caseFormCampaignNumberSub.unsubscribe();
  }

  setFormValidation() {
    //this.caseForm.get('opCode').disable();
    //this.caseForm.get('serviceGroupId').disable();
    this.caseForm.get('warrantySubmissionTypeId').setValidators(Validators.required);
    this.caseForm.get('estimatedTime').setValidators(Validators.required);
    //this.caseForm.get('technicianGroupId').setValidators(Validators.required);
    this.caseForm.get('requestDescription').setValidators(Validators.required);
    this.caseForm.get('techOrTechGroup').setValidators(Validators.required);
    this.caseForm.markAllAsTouched();
  }

  save(appointmentCase: any) {
    this.saveCase.emit({ ...appointmentCase, startTime: moment(appointmentCase.startTime, 'hh:mm').toDate() });
  }

  serviceGroupSelected(event: any, serviceGroups: ServiceGroup[], rooftopId: number) {
    if (event.value == 'lookup') {
      const dialogRef = this.dialog.open(ServiceGroupSelectionComponent, {
        width: '800px',
        disableClose: true,
        data: { serviceGroups: serviceGroups, rooftopId: rooftopId },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.caseForm.get('serviceGroupId').setValue(result.id);
          this.caseForm.get('warrantySubmissionTypeId').setValue(result.warrantySubmissionTypeId);
          this.caseForm.get('estimatedTime').setValue(result.estimatedTime);
          this.caseForm.get('opCode').setValue(result.workOrderLocationOpCode);
          this.caseForm.get('recommendedHours').setValue(result.recommendedHours);
          this.caseForm.get('requestDescription').setValue(result.name);
          this.checkTenths(['estimatedTime', 'recommendedHours']);
        }
      });
    } else {
      if (event.value) {
        let sg: ServiceGroup = serviceGroups.find((s) => s.id == event.value);
        this.caseForm.get('warrantySubmissionTypeId').setValue(sg.warrantySubmissionTypeId);
        this.caseForm.get('estimatedTime').setValue(sg.estimatedTime);
        this.caseForm.get('opCode').setValue(sg.workOrderLocationOpCode);
        this.caseForm.get('recommendedHours').setValue(sg.recommendedHours);
        this.caseForm.get('requestDescription').setValue(sg.name);
        this.checkTenths(['estimatedTime', 'recommendedHours']);
      }
    }
  }

  techOrGroupSelected(event: any, technicians: Employee[], techGroups: TechnicianGroup[]) {
    if (technicians.findIndex((t) => t.associateId == event.value) > -1) {
      let tech: Employee = technicians.find((t) => t.associateId == event.value);
      this.caseForm.get('technicianId').setValue(tech.associateId);
      this.caseForm
        .get('technicianGroupName')
        .setValue(`${tech.embedded.associate.firstName} ${tech.embedded.associate.lastName} `);
      this.caseForm.get('technicianGroupId').setValue(null);
    } else {
      let group: TechnicianGroup = techGroups.find((g) => g.id == event.value);
      this.caseForm.get('technicianGroupId').setValue(group.id);
      this.caseForm.get('technicianGroupName').setValue(group.description);
      this.caseForm.get('technicianId').setValue(null);
    }
  }

  cancel() {
    this.saveCase.emit(null);
  }

  checkTenths(formFields: string[]) {
    formFields.forEach((formField) => {
      let currentValue = this.caseForm.get(formField).value ? this.caseForm.get(formField).value.toString() : null;
      if (currentValue) {
        if (currentValue.length > 0 && !currentValue.includes('.')) {
          this.caseForm.get(formField).setValue(currentValue + '.0');
        } else {
          this.caseForm.get(formField).setValue(currentValue);
        }
      } else {
        this.caseForm.get(formField).setValue('0.0');
      }
    });
  }

  clearServiceGroup() {
    this.caseForm.get('serviceGroupId').setValue(null, { emitEvent: false });
  }

  serviceGroupLookup(serviceGroups: ServiceGroup[], rooftopId: number) {
    const dialogRef = this.dialog.open(ServiceGroupSelectionComponent, {
      width: '800px',
      disableClose: true,
      data: { serviceGroups: serviceGroups, rooftopId: rooftopId },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result != 'null') {
        this.caseForm.get('serviceGroupId').setValue(result.id);
        this.caseForm.get('warrantySubmissionTypeId').setValue(result.warrantySubmissionTypeId);
        this.caseForm.get('estimatedTime').setValue(result.estimatedTime);
        this.caseForm.get('opCode').setValue(result.workOrderLocationOpCode);
        this.caseForm.get('recommendedHours').setValue(result.recommendedHours);
        if (result.technicianId) {
          this.caseForm.get('techOrTechGroup').setValue(result.technicianId);
          this.caseForm.get('technicianId').setValue(result.technicianId);
        } else if (result.technicianGroupId) {
          this.caseForm.get('technicianGroupId').setValue(result.technicianGroupId);
          this.caseForm.get('techOrTechGroup').setValue(result.technicianGroupId);
        }
        this.caseForm.get('requestDescription').setValue(result.name);
        this.checkTenths(['estimatedTime', 'recommendedHours']);
      } else {
        this.caseForm.get('serviceGroupId').setValue(null);
      }
    });
  }

  filterServiceGroups(serviceGroups: ServiceGroup[]): ServiceGroup[] {
    return serviceGroups.filter((sg) => sg.workOrderLocationOpCode != null);
  }
}
